import { useState, useEffect } from "react";
import { Redirect } from "react-router-dom";
import dayjs from "dayjs";
import { useConfig } from "../ConfigProvider";
import ClubPreview from "../ClubPreview";
import { useTunnelData } from "../TunnelContext";
import { useAnalytics } from "../analytics";
import Api from "../baseApi";
import LabelInputText from "../LabelInputText";
import StepHeader from "../StepHeader";
import Button from "../Button";
import Step from "./Step";
import DuplicateSaleWarning from "./DuplicateSaleWarning";
import AutoComplete from "react-google-autocomplete";
import "./UserForm.scss";
import Confirm from "../Confirm";
import StyledStepFooter from "../StyledStepFooter";
import CartSummary from "../CartSummary";
import ProductsLoader from "../ProductsLoader";
import Choice from "../Choice";
import PhoneInput from "react-phone-number-input/react-hook-form";
import { isValidPhoneNumber } from "react-phone-number-input";
import { useForm } from "react-hook-form";
import "react-phone-number-input/style.css";
import "./Phone.css";
import { getErrorMessage } from "../utils/getErrorMessage";
import { useTurnstile, TURNSTILE_PENDING_MESSAGE } from "../hooks/useTurnstile";
import ClubTransferScreen from "./ClubTransfer";

function CgvModal({ url, onClose }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 99999,
      display: 'flex', flexDirection: 'column',
    }}>
      <iframe src={url} style={{ flex: 1, border: 'none', width: '100%' }} title="Conditions générales" />
      <div style={{ display: 'flex', justifyContent: 'center', padding: '0.75rem 1rem', background: '#fff', borderTop: '2px solid #ccc' }}>
        <button
          onClick={onClose}
          style={{
            fontSize: '1.1rem', fontWeight: 700, padding: '0.75rem 3rem',
            cursor: 'pointer', border: '2px solid #333', borderRadius: '4px', background: '#fff',
          }}
        >
          ✕ Fermer
        </button>
      </div>
    </div>
  );
}

const OPTIN_KEYS = [
  "acceptLegal",
  "acceptNewsletter",
  "acceptAdvertising",
  "acceptPartner",
];

// Pour avoir tous les optins à false lors du chargement de la page
const defaultOptins = OPTIN_KEYS.reduce((acc, key) => {
  acc[key] = false;
  return acc;
}, {});

export default function UserFormSpecific() {
  const config = useConfig();
  const C = config.steps.userForm;
  if (C.displayFirstOffer) {
    return (
      <ProductsLoader allowNoUser={true}>
        <UserFormWithOffer />
      </ProductsLoader>
    );
  }
  return <UserForm />;
}

function UserFormWithOffer() {
  const { tunnel } = useTunnelData();
  if (tunnel.club.offerUrl) {
    return (
      <div>
        <UserForm offer_url={tunnel.club.offerUrl} />
      </div>
    );
  }
  const offer = tunnel.products.plans[0];
  return (
    <div>
      <UserForm offer={offer} />
    </div>
  );
}

function getLeadSource(tunnel) {
  if (tunnel.utm_source === "CHATBOT_IG") {
    return "CHATBOT_IG";
  }

  return "MSDS_R2_VEL";
}

function UserForm({ offer, offer_url }) {
  const {
    control,
    watch
  } = useForm({
    defaultValues: {
      phone: "",
    },
    mode: "onChange",
  });

  const config = useConfig();
  const C = config.steps.userForm;
  const { tunnel, updateTunnelUser, updateTunnelContactResa, createSale } = useTunnelData();
  const { trackUserFormCompleted } = useAnalytics();
  const captcha = useTurnstile(config.turnstile?.siteKey);
  const [step, setStep] = useState("FORM_1");
  const [errors, setErrors] = useState([]);
  const [errorMsg, errorMsgSet] = useState(false);
  const [duplicate, setDuplicate] = useState(null);
  const [cgvModalUrl, setCgvModalUrl] = useState(null);
  const [phone] = watch(["phone"]);

  // Make sure optins are reset on page load
  useEffect(() => {
    updateTunnelUser({optins: defaultOptins})
  // On veut que ça run que une fois au mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onChange = (ev) => {
    const { name, value } = ev.target;

    if (OPTIN_KEYS.includes(name)) {
      updateTunnelUser({
        optins: {
          ...defaultOptins,
          ...tunnel.user?.optins,
          [name]: !tunnel.user?.optins?.[name] // toggle
        }
      })
    } else {
      updateTunnelUser({
        [name]: value
      })
    }

    errorMsgSet(false);
    setErrors(prev => prev.filter((e) => e !== name));
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  useEffect(() => {
    onChange({ target: { name: "phone", value: phone } });
  }, [phone]); // eslint-disable-line
  // il est pas question de mettre onChange en useCallback sous peine de boucles infinies

  if (!tunnel.club) return <Redirect to={"/"} />;

  const findInGooglePlace = (place, type) => {
    const compo = place.address_components.find((c) => c.types.indexOf(type) !== -1);
    return compo?.long_name;
  };

  const addressSelected = (googlePlace) => {
    const street_number = findInGooglePlace(googlePlace, "street_number");
    const route = findInGooglePlace(googlePlace, "route");
    const postal_code = findInGooglePlace(googlePlace, "postal_code");
    const locality = findInGooglePlace(googlePlace, "locality");
    let country = findInGooglePlace(googlePlace, "country");
    if (country === "La Réunion" || country === "Guadeloupe") country = "France";

    updateTunnelUser({
      addressFull: googlePlace.formatted_address,
      address: [street_number, route].filter((e) => e).join(" "),
      address2: "",
      zipcode: postal_code,
      city: locality,
      country: country,
    });
    if (!postal_code) {
      if (!street_number) errorMsgSet("Veuillez inclure le numéro de rue dans votre adresse");
      else errorMsgSet("Nous ne trouvons pas le code postal");
    } else {
      errorMsgSet(false);
    }
  };

  const isFirstPartValid = () => {
    errorMsgSet(false);
    const u = tunnel.user || {};
    const errorsList = [];
    const fields = ["firstName", "lastName", "email"];
    if (!C.noGender) {
      fields.push("gender");
    }
    fields.forEach((f) => {
      if (!u[f] && errorsList.indexOf(f) < 0) errorsList.push(f);
    });
    if (C?.optins?.acceptLegal?.required && C?.optins?.acceptLegal?.step === 1 && !tunnel.user?.optins?.acceptLegal) {
      errorMsgSet('Veuillez accepter les conditions générales');
      errorsList.push("acceptLegal");
    }
    if (!u.phone || !isValidPhoneNumber(u.phone)) {
      errorMsgSet("Veuillez saisir votre numéro de téléphone au format international.");
      errorsList.push("phone");
    }
    setErrors(errorsList);

    return !errorsList.length;
  };

  const isSecondPartValid = () => {
    errorMsgSet(false);
    const u = tunnel.user || {};
    const errorsList = [];

    if (!C.noAddress) {
      ["addressFull", "address", "zipcode", "city", "country"].forEach((f) => {
        if (!u[f]) {
          errorsList.push("address");
          errorMsgSet("L'adresse est manquante ou ne contient pas de numéro dans la rue");
        }
      });
    }

    if (!C.noBirthdate && !u.birthdate) {
      errorsList.push("birthdate");
    }
    if (C?.optin?.step === 2 && !tunnel.user?.optin) {
      errorsList.push("optin");
    }

    if (C.minAge) {
      const age = dayjs(new Date()).diff(u.birthdate, "year");
      if (age < C.minAge.value) {
        errorMsgSet(C.minAge.text);
        errorsList.push("birthdate");
      }
    }
    setErrors(errorsList);
    return !errorsList.length;
  };

  const doCreateSale = async (contact, confirmDuplicate = false) => {
    // Appele depuis l'ecran DUPLICATE : une erreur doit revenir au formulaire, qui affiche errorMsg.
    const previousStep = confirmDuplicate ? "FORM_1" : step;
    errorMsgSet(false);
    setStep("CREATING_SALE");
    return createSale(contact, { confirmDuplicate })
      .then((ret) => {
        if (config?.photo?.step === 'Contact') {
          setStep("PHOTO");
        } else if (config.recap) {
          setStep("RECAP");
        } else {
          setStep("SEPA");
        }
        return true;
      })
      .catch((err) => {
        if (err.errorCode === "SALE_ALREADY_SIGNED_RECENTLY") {
          setDuplicate({ payment: err.details, contact });
          setStep("DUPLICATE");
          return false;
        }
        errorMsgSet(config.errorCode[err.errorCode] || err.userMsg || "Une erreur est survenue");
        setStep(previousStep);
        console.error(err);
        return false;
      });
  };

  const submit = async () => {
    const reuseContact = tunnel.contactResa && !tunnel.user.hasChanged;
    if (!reuseContact && captcha.enabled && !captcha.token) {
      errorMsgSet(TURNSTILE_PENDING_MESSAGE);
      return;
    }

    setStep("LOADING");
    errorMsgSet(null);
    setErrors([]);

    if (reuseContact) {
      if (config.plans.beforeUser) {
        return doCreateSale();
      } else {
        setStep("NEXT");
      }
      window.scrollTo(0, 0);
      return;
    }

    const apiBody = {
      tunnelUid: tunnel.uid,
      trackerUid: tunnel.trackerUid,
      msdsId: tunnel.club.msdsId,
      utm_source: tunnel.utm_source,
      utm_campaign: tunnel.utm_campaign,
      utm_medium: tunnel.utm_medium,
      user: {
        lastname: tunnel.user.lastName,
        firstname: tunnel.user.firstName,
        phone: tunnel.user.phone,
        email: tunnel.user.email,
      },
      optins: tunnel.user.optins,
      source: getLeadSource(tunnel),
      sourceUrl: window.location.href,
    };
    if (!C.noAddress) {
      apiBody.user.address = {
        streetAddress: `${tunnel.user.address} ${!!tunnel.user.address2 ? tunnel.user.address2 : ""}`.trim(),
        postalCode: tunnel.user.zipcode,
        addressLocality: tunnel.user.city,
        addressCountry: tunnel.user.country,
      };
    }
    if (!C.noGender) {
      apiBody.user.gender = tunnel.user.gender;
    }
    if (!C.noBirthdate) {
      apiBody.user.birthdate = tunnel.user.birthdate;
    }
    const [query] = Api.POST({
      path: "/api/vel/resamania2/contacts",
      body: apiBody,
      headers: captcha.headers,
    });
    captcha.reset();
    query
      .then(({ contact }) => {
        updateTunnelContactResa(contact);
        trackUserFormCompleted();
        try {
          localStorage.setItem("user", JSON.stringify(tunnel.user));
        } catch (e) {
          //Don't care
        }
        if (config.plans.beforeUser) {
          console.debug("doCreateSale with", contact);
          return doCreateSale(contact).then(() => {
            console.log("doCreateSale has this contactResa", tunnel.contactResa);
          });
        } else {
          setStep("NEXT");
        }
      })
      .catch((err) => {
        console.log("[UserForm] Contact creation error:", err);
        console.log("[UserForm] err.resamaniaCode:", err.resamaniaCode);
        console.log("[UserForm] clubTransfer.enabled:", config.clubTransfer?.enabled);
        console.log("[UserForm] clubTransfer.errorCode:", config.clubTransfer?.errorCode);
        console.log("[UserForm] match:", err.resamaniaCode === config.clubTransfer?.errorCode);
        if (config.clubTransfer?.enabled && err.resamaniaCode === config.clubTransfer.errorCode) {
          console.log("[UserForm] -> Redirecting to CLUB_TRANSFER step");
          setStep("CLUB_TRANSFER");
        } else {
          console.log("[UserForm] -> Showing error message, staying on FORM_1");
          setStep("FORM_1");
          errorMsgSet(getErrorMessage(config, err));
        }
      });
  };

  const getError = (name) => (errors.indexOf(name) >= 0 ? "error" : "");

  const afterStep1 = () => {
    if (!C.noBirthdate || !C.noAddress) {
      setStep("FORM_2");
    } else {
      submit();
    }
  };

  let nextStep = () => {
    throw new Error("No next step");
  };
  if (step === "RECAP") {
    nextStep = () => setStep("SEPA");
  } else if (
    ["CREATING_SALE", "LOADING", "FORM_1", "FORM_2", "LOADING", "NEXT", "SEPA", "PHOTO"].indexOf(step) !== -1
  ) {
    //There is no nextStep for thos
  } else if (step === "SEPA") {
    //There is no nextStep for thos
  } else {
    console.warn("Cannot create nextStep for step ===", step);
  }

  if (step === "NEXT") return <Redirect push to={"/plans"} />;

  if (step === "PHOTO") return <Redirect push to={"/photo"} />;

  if (step === "LOADING") {
    return (
      <Step
        classe="UserForm"
        step="Vos coordonnées"
        loading={config.loadingTextUserCreation}
        style={{ height: "100vh" }}
      />
    );
  }

  if (step === "DUPLICATE") {
    return (
      <DuplicateSaleWarning
        classe="UserForm"
        payment={duplicate?.payment}
        onContinue={() => doCreateSale(duplicate.contact, true)}
        onCancel={() => { setDuplicate(null); setStep("FORM_1"); }}
      />
    );
  }

  if (step === "CREATING_SALE") {
    return <Step classe="UserForm" step="Vos coordonnées" loading={config.loadingTextCartCreation} />;
  }

  if (step === "RECAP") {
    return <CartSummary goNext={nextStep} errorMsg={errorMsg} errorMsgSet={errorMsgSet} />;
  }

  if (step === "SEPA") {
    return <Redirect push to={"/sepa"} />;
  }

  if (step === "CLUB_TRANSFER") {
    return (
      <ClubTransferScreen
        onCancel={() => { setStep("FORM_1"); errorMsgSet(false); }}
        onComplete={() => { errorMsgSet(false); submit(); }}
      />
    );
  }

  if (step === "CHECK_ADDRESS") {
    return <Step classe="UserForm" step="Vos coordonnées" loading={config.loadingTextAddressCheck} />;
  }
  if (step === "FORM_1") {
    return (
      <Step classe="UserForm" step="Vos coordonnées" loading={false}>
        <header>
          <StepHeader text={C.title || "Vos coordonnées (1/2)"} />
        </header>
        <div className={!!offer || !!offer_url ? "split withOffer" : "split"}>
          {offer_url && (
            <img
              src={offer_url}
              alt="offer"
              style={{ marginRight: "auto", background: "white", border: "1px solid #B8B8B8" }}
            />
          )}
          {offer && !offer_url && (
            <Choice
              value={offer}
              selected={[]}
              onSelect={null}
              useImage={config.plans.useImg}
              imageBaseURL={config.plans.imageBaseURL}
            />
          )}
          <form onSubmit={submit}>
            <fieldset>
              {!C.noGender && (
                <label className="labelSpe">
                  <select
                    className={getError("gender")}
                    required
                    onChange={onChange}
                    name={"gender"}
                    value={tunnel.user?.gender ?? ""}
                  >
                    <option value={""} disabled>
                      {C.genderLabel}
                    </option>
                    <option value={"F"}>{C.gender_F}</option>
                    <option value={"M"}>{C.gender_M}</option>
                  </select>
                </label>
              )}
              <LabelInputText
                classError={getError("firstName")}
                onChange={onChange}
                name="firstName"
                placeHolder={C.firstnameLabel}
                value={tunnel.user?.firstName}
              />
              <LabelInputText
                classError={getError("lastName")}
                onChange={onChange}
                name="lastName"
                placeHolder={C.lastnameLabel}
                value={tunnel.user?.lastName}
              />
              <LabelInputText
                classError={getError("email")}
                onChange={onChange}
                name="email"
                type="email"
                placeHolder={C.emailLabel}
                value={tunnel.user?.email}
              />
              <div className="phoneWrapper">
                <PhoneInput
                  name="phone"
                  control={control}
                  international
                  defaultCountry={tunnel?.club?.country}
                  countryCallingCodeEditable={false}
                  placeholder={C.phoneLabel}
                />
                <em>{C.phoneExplain}</em>
              </div>
              {C?.optins?.acceptNewsletter?.text && C?.optins?.acceptNewsletter?.step === 1 && (
                <Confirm
                  className={`labelSpe`}
                  checked={!!tunnel.user?.optins?.acceptNewsletter}
                  toggle={() => onChange({ target: { name: "acceptNewsletter", value: !tunnel.user?.optins?.acceptNewsletter } })}
                  name="acceptNewsletter"
                >
                  <p style={{ fontStyle: "normal", fontSize: "0.875rem", fontWeight: 500, lineHeight: "1.15rem" }}>
                    {C.optins?.acceptNewsletter?.text}
                    </p>
                  {C.optins?.acceptNewsletter?.subtext && (
                    <p style={{ fontStyle: "italic", fontSize: "0.875rem", fontWeight: 300, lineHeight: "1.15rem" }}>{C.optins.acceptNewsletter.subtext}</p>
                  )}
                </Confirm>
              )}
              {C?.optins?.acceptAdvertising?.text && C?.optins?.acceptAdvertising?.step === 1 && (
                <Confirm
                  className={`labelSpe`}
                  checked={!!tunnel.user?.optins?.acceptAdvertising}
                  toggle={() => onChange({ target: { name: "acceptAdvertising", value: !tunnel.user?.optins?.acceptAdvertising } })}
                  name="acceptAdvertising"
                >
                  <p style={{ fontStyle: "normal", fontSize: "0.875rem", fontWeight: 500, lineHeight: "1.15rem" }}>
                    {C.optins?.acceptAdvertising?.text}
                    </p>
                  {C.optins?.acceptAdvertising?.subtext && (
                    <p style={{ fontStyle: "italic", fontSize: "0.875rem", fontWeight: 300, lineHeight: "1.15rem" }}>{C.optins.acceptAdvertising.subtext}</p>
                  )}
                </Confirm>
              )}
              {C?.optins?.acceptPartner?.text && C?.optins?.acceptPartner?.step === 1 && (
                <Confirm
                  className={`labelSpe`}
                  checked={!!tunnel.user?.optins?.acceptPartner}
                  toggle={() => onChange({ target: { name: "acceptPartner", value: !tunnel.user?.optins?.acceptPartner } })}
                  name="acceptPartner"
                >
                  <p style={{ fontStyle: "normal", fontSize: "0.875rem", fontWeight: 500, lineHeight: "1.15rem" }}>
                    {C.optins?.acceptPartner?.text}
                    </p>
                  {C.optins?.acceptPartner?.subtext && (
                    <p style={{ fontStyle: "italic", fontSize: "0.875rem", fontWeight: 300, lineHeight: "1.15rem" }}>{C.optins.acceptPartner.subtext}</p>
                  )}
                </Confirm>
              )}
            </fieldset>
          </form>
        </div>
        <StyledStepFooter className="StyledStepFooter">
          {C?.optins?.acceptLegal?.text && C?.optins?.acceptLegal?.step === 1 && (
            <Confirm
              className={`labelSpe ${getError("acceptLegal")}`}
              checked={!!tunnel.user?.optins?.acceptLegal}
              toggle={() => onChange({ target: { name: "acceptLegal", value: !tunnel.user?.optins?.acceptLegal } })}
            >
              <div>
                <p style={{ fontStyle: "italic", fontSize: "0.875rem", fontWeight: 300, lineHeight: "1.15rem", margin: 0 }}>
                  {C.optins.acceptLegal.text}
                </p>
                {C.optins.acceptLegal?.href && C.optins.acceptLegal?.openInModal ? (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setCgvModalUrl(tunnel?.club?.optin_href || C.optins.acceptLegal?.href); }}
                    style={{ display: 'block', marginTop: '0.35rem', fontSize: "0.875rem", fontWeight: 600, textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                  >
                    {C.optins.acceptLegal.moreLabel}
                  </button>
                ) : C.optins.acceptLegal?.href ? (
                  <a
                    style={{ display: 'block', marginTop: '0.35rem', fontSize: "0.875rem", fontWeight: 600, textDecoration: 'underline' }}
                    href={tunnel?.club?.optin_href || C.optins.acceptLegal?.href}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {C.optins.acceptLegal.moreLabel}
                  </a>
                ) : null}
              </div>
            </Confirm>
          )}
          {cgvModalUrl && <CgvModal url={cgvModalUrl} onClose={() => setCgvModalUrl(null)} />}
          {!C.noClub && (
            <ClubPreview club={tunnel.club} style={{ marginTop: 0, paddingTop: 0, marginBottom: "1rem" }} />
          )}
          {!!errorMsg && <p style={{ textAlign: "center", fontWeight: "bold" }} className="errorMsg">{errorMsg}</p>}
          {step !== "LOADING" && (
            <Button variant="primary" onClick={() => isFirstPartValid() && afterStep1()}>
              {C.nextLabel}
            </Button>
          )}
        </StyledStepFooter>
      </Step>
    );
  }

  if (step === "FORM_2") {
    return (
      // Offer Goal Conversion: CPL
      <Step classe="UserForm" step="Vos coordonnées" loading={false} style={{ height: "100vh" }}>
        {C?.tracker && C.tracker(tunnel)}
        <header>
          <StepHeader text={C.title || "Vos coordonnées (2/2)"} />
        </header>
        <div className="split-second-page">
          <form onSubmit={submit}>
            <fieldset>
              {!C.noBirthdate && (
                <label className="labelSpe">
                  <span>{C.birthdateLabel}</span>
                  <input
                    className={getError("birthdate")}
                    onChange={onChange}
                    name={"birthdate"}
                    type="date"
                    value={tunnel.user?.birthdate ?? ""}
                  />
                </label>
              )}
              {!C.noAddress && (
                <div className={getError("address")}>
                  {/*  Language must be 'FR' cause the API wants country in french name*/}
                  <AutoComplete
                    placeholder={tunnel.user?.addressFull || C.addressLabel}
                    apiKey={"AIzaSyAFPrGdjW4E9zth5knmPfVhwPyaNNDhn1Y"}
                    language="fr"
                    options={{
                      types: ["address"],
                      fields: ["address_components", "formatted_address", "types", "icon", "name"],
                    }}
                    onPlaceSelected={(place) => addressSelected(place)}
                  />
                </div>
              )}
              <Button style={{ marginTop: "2rem" }} disabled={step === "LOADING"} onClick={() => setStep("FORM_1")}>
                {C.backLabel}
              </Button>
            </fieldset>
          </form>
        </div>

        <StyledStepFooter className="StyledStepFooter">
          {C?.optin?.step !== 2 && !C.noClub && (
            <ClubPreview club={tunnel.club} style={{ marginTop: 0, paddingTop: 0, marginBottom: "1rem" }} />
          )}
          {C?.optin?.step === 2 && (
            <Confirm
              className={`labelSpe ${getError("optin")}`}
              checked={!!tunnel.user?.optin}
              toggle={() => onChange({ target: { name: "optin", value: !tunnel.user?.optin } })}
            >
              <p>
                <span style={{ fontStyle: "italic", fontSize: "0.875rem", fontWeight: 300, lineHeight: "1.15rem" }}>
                  {C.optin.text}
                </span>
                <a
                  style={{ fontSize: "0.875rem", fontWeight: 500, lineHeight: "1.15rem", paddingBottom: "0.5rem" }}
                  href={tunnel?.club?.optin_href || C.optin.href}
                  target="_blank"
                  rel="noreferrer"
                >
                  {C.optin.moreLabel}
                </a>
              </p>
            </Confirm>
          )}
          {!!errorMsg && <p style={{ textAlign: "center", fontWeight: "bold" }}>{errorMsg}</p>}
          {step !== "LOADING" && (
            <Button variant="primary" onClick={() => isSecondPartValid() && submit()}>
              {C.nextLabel}
            </Button>
          )}
        </StyledStepFooter>
      </Step>
    );
  }
}
